/**
 * AddEditExperimentV2 — create / edit form for an A/B experiment (V2).
 *
 * The page chrome (ExperimentAddEditPageV2) owns the Save button and
 * fires this form via form="experiment-form" + type="submit".
 *
 * There is no single-GET admin endpoint, so edit-mode loads the
 * experiment by selecting it from the fetched list by slug. On edit we
 * also fetch the per-variant assignment summary for the progress panel.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { QRCodeSVG } from 'qrcode.react';

import LoadingBackdrop from '../../Common/LoadingBackdrop';
import {
  fetchAllExperiments,
  fetchExperimentSummary,
  saveExperiment,
  declareExperimentWinner,
} from '../../../actions/experimentActions';
import { preventEnterFormSubmission } from '../../../utils/formUtils';
import {
  preLaunchFailures,
  preLaunchWarnings,
  STOP_RULE_COUNTER_SOURCES,
} from '../../../utils/experimentSpecChecks';

import DeclareWinnerModal from './DeclareWinnerModal';
import { Button, cn } from '../../redesign';
import {
  FormSection,
  FieldBlock,
  FieldRow,
  TextInput,
  Textarea,
  Select,
  TagsInput,
  DatePicker,
} from '../../redesign/edit';

const STATUSES = ['draft', 'running', 'paused', 'completed'];

// A decided experiment's status can only move to completed — the status
// `<Select>` intentionally cannot reach `decided` itself (that transition
// only happens through the dedicated "Declare winner" action + modal).
const DECIDED_STATUS_OPTIONS = [
  { value: 'decided', label: 'Decided' },
  { value: 'completed', label: 'Completed' },
];

// Statuses the "Declare winner" action is offered from — mirrors the
// server's gate (PUT .../{slug} with status: 'decided' is only allowed
// while running or paused).
const DECLARABLE_STATUSES = ['running', 'paused'];

const PLATFORMS = [
  { value: 'all', label: 'All Platforms' },
  { value: 'ios', label: 'iOS' },
  { value: 'android', label: 'Android' },
];

// `not_worse_lagged` is evaluated after a maturation window (it blocks
// final close-out, not the primary verdict) — see the analysis-spec
// runbook. Kept alongside `not_worse` with a clearer label.
const GUARDRAIL_DIRECTIONS = [
  { value: 'not_worse', label: 'Not worse (at verdict)' },
  { value: 'not_worse_lagged', label: 'Not worse (lagged — checked after maturation)' },
];

const DEFAULT_FORM = {
  slug: '',
  name: '',
  description: '',
  status: 'draft',
  platform: 'all',
  rollout_percentage: 100,
  variants: [
    { name: 'control', weight: 50 },
    { name: 'alternate', weight: 50 },
  ],
};

const DEFAULT_PRIMARY_METRIC = { name: '', definition: '', numerator_event: '', denominator_event: '' };

// Same shape as the primary metric — secondary metrics are diagnostics
// that never gate a verdict, but they are part of the pre-registration
// and so must be authorable here rather than only via a migration.
const DEFAULT_SECONDARY_METRIC = { name: '', definition: '', numerator_event: '', denominator_event: '' };

// Seeded only for brand-new experiments — an existing experiment loads
// whatever guardrails (if any) are already stored in its analysis_spec.
const DEFAULT_GUARDRAILS = [
  { name: 'crash_free_rate', direction: 'not_worse', definition: '' },
  { name: 'onboarding_drop_off', direction: 'not_worse', definition: '' },
];

// The structured fields this form owns. Anything else already present
// in a loaded analysis_spec (future fields added server-side, etc.) is
// preserved verbatim rather than spread-and-possibly-stale, so an
// operator clearing a field (e.g. removing all guardrails) actually
// clears it instead of the original value silently winning a merge.
//
// A key listed here MUST also be loaded into form state by the load
// effect below. Listing a key the form never reads would exclude it
// from the preserve-unknown pass while having nothing to write back —
// i.e. the first save would silently drop it.
const KNOWN_ANALYSIS_SPEC_KEYS = [
  'primary_metric',
  'secondary_metrics',
  'guardrails',
  'planned_sample_size',
  'sample_size_rationale',
  'planned_end_date',
  'amplitude_event_names',
  'amplitude_chart_ids',
  'attribution',
  'stop_rule',
  'baseline',
];

// Options for stop_rule.counter_source — must stay in sync with the
// server's ExperimentAnalysisSpecValidator::STOP_RULE_COUNTER_SOURCES.
const STOP_RULE_COUNTER_OPTIONS = [
  { value: '', label: '— select the counter the stop rule reads —' },
  { value: 'amplitude_exposure_uniques', label: 'Amplitude exposure uniques (prod connector)' },
  { value: 'framework_total_assigned', label: "Framework total_assigned (this CMS's summary)" },
];

// YYYY-MM-DD <-> local Date helpers. Deliberately avoid `new Date(str)`
// / date-fns `parseISO` for a date-only string — both parse it as UTC
// midnight, which renders as the previous day in negative-UTC-offset
// timezones.
function parseDateOnly(str) {
  if (!str) return null;
  const [y, m, d] = String(str).split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}
function formatDateOnly(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

// `ended_at` is a full ISO timestamp (unlike the date-only fields above),
// so it's rendered with `new Date(...)` directly rather than the
// date-only helpers.
function formatEndedAt(isoString) {
  if (!isoString) return null;
  const d = new Date(isoString);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

// Repeated guardrail / secondary-metric rows are held as `{ uid, data }`.
//
// `uid` gives each row a stable identity: keying by array index shifts
// component (and focus) identity onto a different row when an earlier
// one is removed, and leaves every row's controls indistinguishable to a
// screen reader. `data` is the row exactly as the server sent it, plus
// the operator's edits.
//
// The two are kept in separate namespaces deliberately. Stashing the uid
// *inside* the row would reserve a key name in persisted data — a server
// field that happened to share it would be overwritten by UI bookkeeping
// and then stripped on the way out.
let rowUidCounter = 0;
const makeRow = (data) => ({ uid: `row-${(rowUidCounter += 1)}`, data: { ...data } });

// A row the operator has typed into but left unnamed. Rows are dropped
// on save when the name is blank, which silently discards visible
// content — so this is surfaced as a validation error instead. A wholly
// empty row (a freshly added one) is still dropped quietly.
function rowHasContentButNoName(data, contentFields) {
  if ((data.name || '').trim()) return false;
  return contentFields.some((field) => (data[field] || '').trim());
}

// Normalises a row for the payload. Only the fields this form renders are
// trimmed and dropped-when-empty; every other key is passed through
// untouched, including one whose value is an empty string. An opaque
// `aggregation: ""` is server-owned data, and blanking it here would be
// the same silent loss this row plumbing exists to prevent.
//
// Carrying the original object through form state (rather than re-finding
// it by name at save time) is what makes a rename safe: a name-matched
// lookup misses once the name changes.
function buildRowEntry(data, renderedFields) {
  const entry = { ...data };
  renderedFields.forEach((field) => {
    const trimmed = (data[field] || '').trim();
    if (trimmed) {
      entry[field] = trimmed;
    } else {
      delete entry[field];
    }
  });
  return entry;
}

export default function AddEditExperimentV2({ isEdit, selectedSlug, onLoaded }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const experiments = useSelector((s) => s.experiments?.experiments);
  const summary = useSelector((s) => s.experiments?.summary);

  // The loaded SERVER copy of this experiment, kept separate from form
  // state on purpose. The form is the operator's draft; this is what the
  // server currently holds — and the server is what the "Declare winner"
  // action has to agree with, because it validates `winning_variant`
  // against the variants it has stored, not against the draft.
  const serverExperiment = Array.isArray(experiments) && selectedSlug
    ? experiments.find((e) => e.slug === selectedSlug) || null
    : null;

  const [formData, setFormData] = useState(DEFAULT_FORM);
  // The form exactly as it was loaded. Compared against `formData` to
  // detect unsaved operator edits — deliberately a snapshot rather than
  // the live server copy, so a background refetch is not mistaken for
  // the operator having typed something.
  const [loadedForm, setLoadedForm] = useState(null);
  const [primaryMetric, setPrimaryMetric] = useState(DEFAULT_PRIMARY_METRIC);
  const [secondaryMetrics, setSecondaryMetrics] = useState([]);
  const [guardrails, setGuardrails] = useState(() => DEFAULT_GUARDRAILS.map(makeRow));
  const [plannedSampleSize, setPlannedSampleSize] = useState('');
  const [sampleSizeRationale, setSampleSizeRationale] = useState('');
  const [plannedEndDate, setPlannedEndDate] = useState(null);
  const [amplitudeEventNames, setAmplitudeEventNames] = useState([]);
  const [amplitudeChartIds, setAmplitudeChartIds] = useState([]);
  const [attributionAssignmentKey, setAttributionAssignmentKey] = useState('');
  const [stopRuleCounterSource, setStopRuleCounterSource] = useState('');
  const [baselineRate, setBaselineRate] = useState('');
  const [baselineDenominatorEvent, setBaselineDenominatorEvent] = useState('');
  // The originally-loaded analysis_spec, kept around so (a) unknown/
  // foreign keys survive a save untouched and (b) the gate + payload
  // logic below can tell whether the operator actually changed the
  // spec this session vs. is saving an unrelated edit (e.g. rollout %).
  const [analysisSpecOriginal, setAnalysisSpecOriginal] = useState(null);
  const [originalStatus, setOriginalStatus] = useState('draft');
  // Read-only context for the lagging-counter warning — the form never
  // edits assignment_mode, but the mirror needs it (client_reported
  // experiments must not read framework_total_assigned as a stop counter).
  const [assignmentMode, setAssignmentMode] = useState(null);
  // Set the first time any spec-field handler fires. Mirrors the
  // server-side gate (APIv2): a running experiment is only re-validated
  // for spec completeness when the request's analysis_spec is present
  // (touched) or the update transitions the experiment into running.
  const [specTouched, setSpecTouched] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  // Decided-state read-only context — never edited from this form, only
  // set once through the "Declare winner" action.
  const [winningVariant, setWinningVariant] = useState(null);
  const [endedAt, setEndedAt] = useState(null);
  const [declareWinnerOpen, setDeclareWinnerOpen] = useState(false);

  const onLoadedRef = useRef(onLoaded);
  onLoadedRef.current = onLoaded;
  const loadedRef = useRef(false);

  // Edit mode: fetch the list (source of truth) + the live summary.
  useEffect(() => {
    if (!isEdit || !selectedSlug) return;
    dispatch(fetchAllExperiments());
    dispatch(fetchExperimentSummary(selectedSlug));
  }, [dispatch, isEdit, selectedSlug]);

  // Populate the form once the matching experiment arrives. loadedRef
  // guards against clobbering the operator's edits on later store changes.
  useEffect(() => {
    if (!isEdit || loadedRef.current || !Array.isArray(experiments)) return;
    const exp = experiments.find((e) => e.slug === selectedSlug);
    if (!exp) {
      // List loaded but no match — stale/typo'd slug. Don't strand the
      // operator on a blank, slug-locked form; bounce back to the list.
      loadedRef.current = true;
      Swal.fire({ icon: 'warning', text: 'Experiment not found.' })
        .then(() => navigate('/experiments'));
      return;
    }
    loadedRef.current = true;
    const nextForm = {
      slug: exp.slug,
      name: exp.name || '',
      description: exp.description || '',
      status: exp.status || 'draft',
      platform: exp.platform || 'all',
      rollout_percentage: exp.rollout_percentage ?? 100,
      variants: Array.isArray(exp.variants) && exp.variants.length
        ? exp.variants.map((v) => ({ name: v.name, weight: v.weight }))
        : DEFAULT_FORM.variants,
    };
    setFormData(nextForm);
    // Snapshot of exactly what was loaded, used to tell an operator edit
    // from an untouched form (see hasUnsavedChanges).
    setLoadedForm(nextForm);
    setOriginalStatus(exp.status || 'draft');
    setAssignmentMode(exp.assignment_mode || null);
    setWinningVariant(exp.winning_variant || null);
    setEndedAt(exp.ended_at || null);

    // Seed the structured analysis-spec fields from whatever's stored.
    // Keep the raw object around (even if null) so unknown keys survive.
    const spec = exp.analysis_spec || {};
    setAnalysisSpecOriginal(exp.analysis_spec || null);
    setPrimaryMetric({
      name: spec.primary_metric?.name || '',
      definition: spec.primary_metric?.definition || '',
      numerator_event: spec.primary_metric?.numerator_event || '',
      denominator_event: spec.primary_metric?.denominator_event || '',
    });
    // `...m` first so opaque/server-owned keys (e.g. a future
    // `aggregation`) ride along in form state and survive a rename —
    // recovering them by name at save time would miss once renamed.
    setSecondaryMetrics(
      Array.isArray(spec.secondary_metrics)
        ? spec.secondary_metrics.map((m) =>
            makeRow({
              ...m,
              name: m.name || '',
              definition: m.definition || '',
              numerator_event: m.numerator_event || '',
              denominator_event: m.denominator_event || '',
            })
          )
        : []
    );
    // Existing experiments load whatever guardrails they already have —
    // including none — rather than re-seeding the new-experiment defaults.
    // Every field — `definition` and any opaque server-owned key — is
    // loaded into state rather than recovered from the original by name
    // at save time, so renaming a guardrail can't silently drop them.
    setGuardrails(
      Array.isArray(spec.guardrails) && spec.guardrails.length
        ? spec.guardrails.map((g) =>
            makeRow({
              ...g,
              name: g.name || '',
              direction: g.direction || 'not_worse',
              definition: g.definition || '',
            })
          )
        : []
    );
    // Shown verbatim even if non-numeric (legacy data) — see
    // handleSampleSizeChange / buildAnalysisSpec for how a non-numeric
    // value is preserved rather than silently dropped on save.
    setPlannedSampleSize(spec.planned_sample_size != null ? String(spec.planned_sample_size) : '');
    setSampleSizeRationale(spec.sample_size_rationale || '');
    setPlannedEndDate(parseDateOnly(spec.planned_end_date));
    setAmplitudeEventNames(Array.isArray(spec.amplitude_event_names) ? spec.amplitude_event_names : []);
    setAmplitudeChartIds(Array.isArray(spec.amplitude_chart_ids) ? spec.amplitude_chart_ids : []);
    setAttributionAssignmentKey(spec.attribution?.assignment_key || '');
    setStopRuleCounterSource(spec.stop_rule?.counter_source || '');
    // Shown verbatim even if non-numeric (legacy data), like sample size.
    setBaselineRate(spec.baseline?.rate != null ? String(spec.baseline.rate) : '');
    setBaselineDenominatorEvent(spec.baseline?.denominator_event || '');
    setSpecTouched(false);

    onLoadedRef.current?.({ name: exp.name, updated_at: exp.updated_at });
  }, [experiments, isEdit, selectedSlug, navigate]);

  /* ─── Field handlers ─────────────────────────────────────── */
  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // A decided experiment's variants are locked server-side — the server
  // refuses any change to them, so these are no-ops rather than letting
  // the operator edit a field that a save would then reject.
  //
  // Field locking (and the payload omission below) is keyed on the
  // *status*, because that is exactly what the server's own lock is
  // keyed on: once the operator archives the experiment to `completed`
  // it is editable again. The DECISION is keyed on `winning_variant`
  // instead (see `hasDecision`) — it outlives the `decided` status and
  // must stay visible after archiving.
  const isDecided = originalStatus === 'decided';
  const hasDecision = !!winningVariant;

  const handleVariantChange = (index, field, value) => {
    if (isDecided) return;
    setFormData((prev) => {
      const variants = prev.variants.map((v, i) => (i === index ? { ...v, [field]: value } : v));
      return { ...prev, variants };
    });
  };

  const addVariant = () => {
    if (isDecided) return;
    setFormData((prev) => ({ ...prev, variants: [...prev.variants, { name: '', weight: 0 }] }));
  };

  const removeVariant = (index) => {
    if (isDecided) return;
    setFormData((prev) => ({ ...prev, variants: prev.variants.filter((_, i) => i !== index) }));
  };

  const handlePrimaryMetricChange = (e) => {
    const { name, value } = e.target;
    setPrimaryMetric((prev) => ({ ...prev, [name]: value }));
    setSpecTouched(true);
  };

  const handleSecondaryMetricChange = (index, field, value) => {
    setSecondaryMetrics((prev) =>
      prev.map((m, i) => (i === index ? { ...m, data: { ...m.data, [field]: value } } : m))
    );
    setSpecTouched(true);
  };

  const addSecondaryMetric = () => {
    setSecondaryMetrics((prev) => [...prev, makeRow(DEFAULT_SECONDARY_METRIC)]);
    setSpecTouched(true);
  };

  const removeSecondaryMetric = (index) => {
    setSecondaryMetrics((prev) => prev.filter((_, i) => i !== index));
    setSpecTouched(true);
  };

  const handleGuardrailChange = (index, field, value) => {
    setGuardrails((prev) =>
      prev.map((g, i) => (i === index ? { ...g, data: { ...g.data, [field]: value } } : g))
    );
    setSpecTouched(true);
  };

  const addGuardrail = () => {
    setGuardrails((prev) => [...prev, makeRow({ name: '', direction: 'not_worse', definition: '' })]);
    setSpecTouched(true);
  };

  const removeGuardrail = (index) => {
    setGuardrails((prev) => prev.filter((_, i) => i !== index));
    setSpecTouched(true);
  };

  // Digits only, typed as the operator types — keeps new/edited values
  // clean. A legacy non-numeric value loaded from an existing spec is
  // shown as-is (see the load effect) until the operator touches it.
  const handleSampleSizeChange = (e) => {
    setPlannedSampleSize(e.target.value.replace(/[^0-9]/g, ''));
    setSpecTouched(true);
  };

  const handleSampleSizeRationaleChange = (e) => {
    setSampleSizeRationale(e.target.value);
    setSpecTouched(true);
  };

  const handlePlannedEndDateChange = (date) => {
    setPlannedEndDate(date);
    setSpecTouched(true);
  };

  const handleAmplitudeEventNamesChange = (next) => {
    setAmplitudeEventNames(next);
    setSpecTouched(true);
  };

  const handleAmplitudeChartIdsChange = (next) => {
    setAmplitudeChartIds(next);
    setSpecTouched(true);
  };

  const handleAttributionAssignmentKeyChange = (e) => {
    setAttributionAssignmentKey(e.target.value);
    setSpecTouched(true);
  };

  const handleStopRuleCounterSourceChange = (e) => {
    setStopRuleCounterSource(e.target.value);
    setSpecTouched(true);
  };

  // Digits and at most one decimal point — a baseline rate is a
  // percentage, and admitting a second dot would make a *typed* value
  // non-numeric, which the save-time legacy-preserve fallback below cannot
  // distinguish from an untouched legacy load (it would silently save the
  // old value while the field shows the typo).
  const handleBaselineRateChange = (e) => {
    const raw = e.target.value.replace(/[^0-9.]/g, '');
    const firstDot = raw.indexOf('.');
    const clean = firstDot === -1 ? raw : raw.slice(0, firstDot + 1) + raw.slice(firstDot + 1).replace(/\./g, '');
    setBaselineRate(clean);
    setSpecTouched(true);
  };

  const handleBaselineDenominatorEventChange = (e) => {
    setBaselineDenominatorEvent(e.target.value);
    setSpecTouched(true);
  };

  /* ─── Submit ─────────────────────────────────────────────── */
  const buildAnalysisSpec = () => {
    // primary_metric / each guardrail spread their originally-loaded
    // object first, so nested foreign keys the form doesn't render
    // (e.g. a guardrail `definition`, a primary_metric `mde`) survive a
    // round-trip instead of being rebuilt from scratch.
    const originalPrimaryMetric = analysisSpecOriginal?.primary_metric || {};
    const primaryMetricSpec = {
      ...originalPrimaryMetric,
      name: primaryMetric.name.trim(),
      definition: primaryMetric.definition.trim(),
      numerator_event: primaryMetric.numerator_event.trim(),
      denominator_event: primaryMetric.denominator_event.trim(),
    };
    Object.keys(primaryMetricSpec).forEach((key) => {
      if (primaryMetricSpec[key] === '') delete primaryMetricSpec[key];
    });

    // No name-matched lookup against the originally-loaded spec on
    // either of these: each row carries its own opaque keys in form
    // state, so a rename can't orphan them.
    const secondaryMetricsSpec = secondaryMetrics
      .map((m) => m.data)
      .filter((d) => (d.name || '').trim())
      .map((d) => buildRowEntry(d, ['name', 'definition', 'numerator_event', 'denominator_event']));

    const guardrailsSpec = guardrails
      .map((g) => g.data)
      .filter((d) => (d.name || '').trim())
      .map((d) => {
        const entry = buildRowEntry(d, ['name', 'definition']);
        // `direction` always has a value and is never blanked away.
        entry.direction = d.direction || 'not_worse';
        return entry;
      });

    // A non-numeric value can only reach here if it was loaded from a
    // legacy spec and never touched (the change handler above only ever
    // sets digits). Number(...) would silently become NaN -> null on
    // JSON serialization, so fall back to the original stored value
    // instead of degrading it.
    let plannedSampleSizeValue;
    if (plannedSampleSize === '' || plannedSampleSize == null) {
      plannedSampleSizeValue = undefined;
    } else {
      const n = Number(plannedSampleSize);
      plannedSampleSizeValue = Number.isFinite(n) ? n : (analysisSpecOriginal?.planned_sample_size ?? null);
    }

    // Start from any keys this form doesn't render (future/foreign
    // fields), then lay the structured fields on top — but only when
    // they hold a value, so a cleared field actually clears rather
    // than falling back to a stale original.
    const spec = {};
    Object.entries(analysisSpecOriginal || {}).forEach(([key, value]) => {
      if (!KNOWN_ANALYSIS_SPEC_KEYS.includes(key)) spec[key] = value;
    });
    if (Object.keys(primaryMetricSpec).length) spec.primary_metric = primaryMetricSpec;
    if (secondaryMetricsSpec.length) spec.secondary_metrics = secondaryMetricsSpec;
    if (guardrailsSpec.length) spec.guardrails = guardrailsSpec;
    if (plannedSampleSizeValue !== undefined) spec.planned_sample_size = plannedSampleSizeValue;
    if (sampleSizeRationale.trim()) spec.sample_size_rationale = sampleSizeRationale.trim();

    // attribution / stop_rule / baseline — same pattern as primary_metric:
    // spread the originally-loaded group first so nested foreign keys
    // survive, lay the rendered fields on top, drop the group when empty.
    const attributionSpec = { ...(analysisSpecOriginal?.attribution || {}) };
    if (attributionAssignmentKey.trim()) attributionSpec.assignment_key = attributionAssignmentKey.trim();
    else delete attributionSpec.assignment_key;
    if (Object.keys(attributionSpec).length) spec.attribution = attributionSpec;

    const stopRuleSpec = { ...(analysisSpecOriginal?.stop_rule || {}) };
    if (stopRuleCounterSource) stopRuleSpec.counter_source = stopRuleCounterSource;
    else delete stopRuleSpec.counter_source;
    if (Object.keys(stopRuleSpec).length) spec.stop_rule = stopRuleSpec;

    const baselineSpec = { ...(analysisSpecOriginal?.baseline || {}) };
    if (baselineRate === '' || baselineRate === '.') {
      // A lone dot is typable but carries no number — treat as cleared,
      // never as rate: null (a null rate would arm the baseline failure
      // check for a rate that doesn't exist).
      delete baselineSpec.rate;
    } else {
      // Same legacy-value guard as planned_sample_size: with the one-dot
      // input filter, a non-numeric value can only be an untouched legacy
      // load — preserve it rather than degrading it.
      const n = Number(baselineRate);
      baselineSpec.rate = Number.isFinite(n) ? n : (analysisSpecOriginal?.baseline?.rate ?? null);
    }
    if (baselineDenominatorEvent.trim()) baselineSpec.denominator_event = baselineDenominatorEvent.trim();
    else delete baselineSpec.denominator_event;
    if (Object.keys(baselineSpec).length) spec.baseline = baselineSpec;

    const hasAnyContent =
      Object.keys(spec).length > 0 || !!plannedEndDate || amplitudeEventNames.length > 0 || amplitudeChartIds.length > 0;
    if (!hasAnyContent) return null;

    // Always emit these three so a saved spec matches the documented
    // template shape rather than omitting keys the server expects.
    spec.planned_end_date = plannedEndDate ? formatDateOnly(plannedEndDate) : null;
    spec.amplitude_event_names = amplitudeEventNames;
    spec.amplitude_chart_ids = amplitudeChartIds;

    return spec;
  };

  const buildPayload = () => {
    const payload = {
      isEdit: !!isEdit,
      slug: formData.slug.trim(),
      name: formData.name.trim(),
      description: formData.description?.trim() || null,
      status: formData.status,
    };
    // The server refuses changes to variants/rollout_percentage/platform
    // once decided (a 422) — omit them rather than resend the unchanged,
    // now-disabled values, which mirrors the server's own field lock.
    if (!isDecided) {
      payload.platform = formData.platform;
      payload.rollout_percentage = Number(formData.rollout_percentage);
      payload.variants = formData.variants.map((v) => ({ name: v.name.trim(), weight: Number(v.weight) }));
    }
    // In edit mode, only send analysis_spec when the operator actually
    // touched a spec field this session. Mirrors the server: an
    // unrelated edit (e.g. a rollout tweak) to an already-running
    // experiment must not re-gate on legacy/incomplete spec data it
    // never asked to change. Create mode always includes it.
    if (!isEdit || specTouched) {
      payload.analysis_spec = buildAnalysisSpec();
    }
    return payload;
  };

  const validate = (payload) => {
    if (!payload.slug) return 'Slug is required.';
    if (!payload.name) return 'Name is required.';
    // A decided save omits variants/rollout_percentage entirely (the
    // server refuses changes to them) — nothing to validate here.
    if (!isDecided) {
      if (payload.variants.length < 2) return 'At least two variants are required.';
      if (payload.variants.some((v) => !v.name)) return 'Every variant needs a name.';
      if (payload.variants.reduce((sum, v) => sum + (v.weight || 0), 0) <= 0) {
        return 'At least one variant must have a weight greater than 0.';
      }
      if (Number.isNaN(payload.rollout_percentage) || payload.rollout_percentage < 0 || payload.rollout_percentage > 100) {
        return 'Rollout % must be between 0 and 100.';
      }
    }

    // Unnamed rows are dropped when the spec is built. That is right for
    // a freshly added blank row, but silently discards a row the
    // operator actually filled in — so those are surfaced here instead
    // of vanishing on save. Applies to draft saves too, which is where
    // the loss would otherwise go completely unremarked.
    //
    // Gated to match `buildPayload`: an untouched edit omits
    // `analysis_spec` entirely, so a pre-existing malformed row is not in
    // the request and must not block an unrelated rollout/name change.
    if (!isEdit || specTouched) {
      if (guardrails.some((g) => rowHasContentButNoName(g.data, ['definition']))) {
        return 'Every guardrail with a definition needs a name.';
      }
      if (
        secondaryMetrics.some((m) =>
          rowHasContentButNoName(m.data, ['definition', 'numerator_event', 'denominator_event'])
        )
      ) {
        return 'Every secondary metric with content needs a name.';
      }
    }

    if (payload.status === 'running') {
      // Mirrors the server's gate exactly: fires when the effective
      // status is running AND (the spec was touched this session OR the
      // update transitions the experiment into running). An untouched
      // edit to an already-running experiment (e.g. rollout %) is not
      // gated, even if the stored spec is incomplete.
      const transitioningIntoRunning = originalStatus !== 'running';
      const gateApplies = specTouched || transitioningIntoRunning;
      if (gateApplies) {
        const effectiveSpec = (!isEdit || specTouched ? buildAnalysisSpec() : analysisSpecOriginal) || {};
        const pm = effectiveSpec.primary_metric || {};
        const missing = [];
        if (!pm.name) missing.push('primary metric name');
        if (!pm.numerator_event) missing.push('primary metric numerator event');
        if (!pm.denominator_event) missing.push('primary metric denominator event');
        if (!Number.isInteger(effectiveSpec.planned_sample_size) || effectiveSpec.planned_sample_size < 1) {
          missing.push('planned sample size (a whole number ≥ 1)');
        }
        if (!Array.isArray(effectiveSpec.guardrails) || effectiveSpec.guardrails.length === 0) {
          missing.push('at least one guardrail');
        }
        if (missing.length) {
          return `Analysis spec is incomplete for a running experiment — missing: ${missing.join(', ')}.`;
        }

        // Pre-launch measurement checks (mirrors the server's
        // ExperimentAnalysisSpecValidator). Failures block outright and
        // can never be acknowledged away; warnings are handled in
        // submit() via the acknowledge dialog.
        const failures = preLaunchFailures(effectiveSpec);
        if (failures.length) {
          return `Pre-launch measurement checks failed: ${failures.join('; ')}.`;
        }
      }
    }
    return null;
  };

  // The effective spec + gate condition, shared by validate() and the
  // warnings-acknowledge flow in submit(). Mirrors the server: the gate
  // fires when the effective status is running AND (the spec was touched
  // this session OR the update transitions the experiment into running).
  const runningGateSpec = (payload) => {
    if (payload.status !== 'running') return null;
    const transitioningIntoRunning = originalStatus !== 'running';
    if (!specTouched && !transitioningIntoRunning) return null;
    return (!isEdit || specTouched ? buildAnalysisSpec() : analysisSpecOriginal) || {};
  };

  const submit = () => {
    const payload = buildPayload();

    const error = validate(payload);
    if (error) {
      Swal.fire({ icon: 'error', text: error });
      return;
    }

    const dispatchSave = () => {
      setIsSaving(true);
      const result = dispatch(saveExperiment(payload, () => navigate('/experiments')));
      if (result && typeof result.finally === 'function') {
        result.finally(() => setIsSaving(false));
      } else {
        setTimeout(() => setIsSaving(false), 3000);
      }
    };

    // Advisory pre-launch warnings (mirrors the server). The server
    // rejects the running transition until they are explicitly
    // acknowledged — so surface them in the confirmation dialog and set
    // acknowledge_spec_warnings only when the operator confirms with
    // them visible.
    const gateSpec = runningGateSpec(payload);
    const warnings = gateSpec ? preLaunchWarnings(gateSpec, { assignmentMode }) : [];
    const escapeHtml = (s) =>
      s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
    const warningsHtml = warnings.length
      ? `<div style="text-align:left;font-size:13px"><p style="margin:0 0 6px">Pre-launch warnings — review before starting:</p><ul style="margin:0;padding-left:18px">${warnings
          .map((w) => `<li style="margin-bottom:4px">${escapeHtml(w)}</li>`)
          .join('')}</ul></div>`
      : null;

    // Confirm only on an actual transition INTO running/completed — not on
    // every save of an already-running experiment (e.g. a rollout tweak).
    const isTransition = payload.status !== originalStatus
      && (payload.status === 'running' || payload.status === 'completed');
    if (isTransition) {
      const verb = payload.status === 'running' ? 'start' : 'complete';
      const baseText = verb === 'start'
        ? 'Setting status to running will begin assigning users to variants.'
        : 'Completing stops new assignments. This is the end state for the test.';
      Swal.fire({
        icon: warningsHtml ? 'warning' : 'question',
        title: `${verb === 'start' ? 'Start' : 'Complete'} experiment?`,
        ...(warningsHtml
          ? { html: `<p style="margin:0 0 10px;font-size:13px">${baseText}</p>${warningsHtml}` }
          : { text: baseText }),
        showConfirmButton: true,
        showCancelButton: true,
        confirmButtonText: warningsHtml && verb === 'start' ? 'Acknowledge & start' : verb === 'start' ? 'Start' : 'Complete',
      }).then((res) => {
        if (res.value) {
          if (warnings.length) payload.acknowledge_spec_warnings = true;
          dispatchSave();
        }
      });
      return;
    }

    // Already-running experiment whose spec was touched this session:
    // there is no transition dialog, but the server still gates on the
    // warnings — acknowledge through a dedicated confirm.
    if (warnings.length) {
      Swal.fire({
        icon: 'warning',
        title: 'Save with spec warnings?',
        html: warningsHtml,
        showConfirmButton: true,
        showCancelButton: true,
        confirmButtonText: 'Acknowledge & save',
      }).then((res) => {
        if (res.value) {
          payload.acknowledge_spec_warnings = true;
          dispatchSave();
        }
      });
      return;
    }

    dispatchSave();
  };

  const handleSubmit = (e) => {
    if (e?.preventDefault) e.preventDefault();
    submit();
  };

  const handleDeclareWinner = (variantName) =>
    dispatch(declareExperimentWinner(selectedSlug, variantName, () => navigate('/experiments')));

  /* ─── Render ─────────────────────────────────────────────── */

  // Declaring a winner navigates straight back to the list, so any
  // pending edit on this form would be thrown away without a word. The
  // action is therefore BLOCKED — not merely warned about — until the
  // draft is clean again.
  //
  // Reuses the dirty tracking that already exists: `specTouched` for the
  // analysis spec (set by every spec handler), plus a field-by-field
  // compare of `formData` against the snapshot taken at load. No parallel
  // "isDirty" flag to keep in sync with the handlers.
  const formDiffersFromLoaded = () => {
    if (!loadedForm) return false;
    if ((formData.name || '').trim() !== (loadedForm.name || '').trim()) return true;
    if ((formData.description || '').trim() !== (loadedForm.description || '').trim()) return true;
    if (formData.status !== loadedForm.status) return true;
    if (formData.platform !== loadedForm.platform) return true;
    if (Number(formData.rollout_percentage) !== Number(loadedForm.rollout_percentage)) return true;
    if (formData.variants.length !== loadedForm.variants.length) return true;
    return formData.variants.some((v, i) => (
      (v.name || '').trim() !== (loadedForm.variants[i].name || '').trim()
      || Number(v.weight) !== Number(loadedForm.variants[i].weight)
    ));
  };
  const hasUnsavedChanges = specTouched || formDiffersFromLoaded();

  const canDeclareWinner = isEdit && DECLARABLE_STATUSES.includes(originalStatus);
  const endedAtLabel = formatEndedAt(endedAt);

  return (
    <>
      <LoadingBackdrop open={isSaving} />
      {/* Variants come from the loaded SERVER copy, never from form
          state: the server validates `winning_variant` against what it
          has stored, so offering an unsaved rename would guarantee a
          rejected PUT. */}
      <DeclareWinnerModal
        open={declareWinnerOpen}
        onClose={() => setDeclareWinnerOpen(false)}
        variants={serverExperiment?.variants}
        onConfirm={handleDeclareWinner}
      />
      <form
        id="experiment-form"
        onSubmit={handleSubmit}
        onKeyDown={preventEnterFormSubmission}
        autoComplete="off"
        style={{ minWidth: 0, maxWidth: 980 }}
      >
        {canDeclareWinner && (
          <div className="flex items-center justify-end" style={{ marginBottom: 16, gap: 12 }}>
            {hasUnsavedChanges && (
              <span className="text-text-muted" style={{ fontSize: 12, textAlign: 'right' }}>
                Save your changes first — declaring a winner leaves this page and discards them.
              </span>
            )}
            <Button
              type="button"
              variant="primary"
              onClick={() => setDeclareWinnerOpen(true)}
              disabled={hasUnsavedChanges}
            >
              Declare winner
            </Button>
          </div>
        )}

        {/* Keyed on the winner, not the status: archiving a decided
            experiment to `completed` must not erase the record of what
            was decided. */}
        {hasDecision && (
          <div
            className="bg-surface border border-navy"
            style={{ borderRadius: 10, padding: '12px 16px', marginBottom: 20 }}
          >
            <div
              className="font-condensed font-bold uppercase text-navy"
              style={{ fontSize: 11, letterSpacing: '0.08em', marginBottom: 4 }}
            >
              {isDecided ? 'Decided' : 'Decided · archived'}
            </div>
            <div className="text-navy" style={{ fontSize: 13.5 }}>
              Winner: <strong>{winningVariant}</strong>
              {endedAtLabel && <>{' · '}{endedAtLabel}</>}
            </div>
            {/* What is ACTUALLY served. The server rewrites a decided
                experiment's client config to the winner at weight 100,
                every other arm 0, rollout 100 — so the stored weights and
                rollout % rendered below are history, not current truth. */}
            <div className="text-navy" style={{ fontSize: 12.5, marginTop: 6, lineHeight: 1.5 }}>
              {isDecided ? (
                <>
                  Serving <strong>{winningVariant}</strong> to 100% of new devices — every other
                  variant at 0%. The variant weights and rollout % below are the stored values
                  from before the decision, kept for the record.
                </>
              ) : (
                <>Completed — no longer served. The winner above is kept for the record.</>
              )}
            </div>
          </div>
        )}

        <FormSection title="Details" num="01">
          <FieldRow>
            <FieldBlock label="Slug" hint={isEdit ? 'cannot be changed' : 'lowercase, e.g. alternate_onboarding'} htmlFor="f-exp-slug">
              <TextInput
                id="f-exp-slug"
                name="slug"
                value={formData.slug}
                onChange={handleFieldChange}
                disabled={isEdit}
                placeholder="alternate_onboarding"
              />
            </FieldBlock>
            <FieldBlock label="Name" htmlFor="f-exp-name">
              <TextInput id="f-exp-name" name="name" value={formData.name} onChange={handleFieldChange} />
            </FieldBlock>
          </FieldRow>
          <FieldBlock label="Description" htmlFor="f-exp-desc">
            <Textarea id="f-exp-desc" name="description" value={formData.description || ''} onChange={handleFieldChange} rows={2} />
          </FieldBlock>
          <FieldRow>
            <FieldBlock label="Status" htmlFor="f-exp-status">
              <Select id="f-exp-status" name="status" value={formData.status} onChange={handleFieldChange}>
                {isDecided
                  ? DECIDED_STATUS_OPTIONS.map((s) => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))
                  : STATUSES.map((s) => (
                      <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                    ))}
              </Select>
            </FieldBlock>
            <FieldBlock label="Platform" htmlFor="f-exp-platform">
              <Select id="f-exp-platform" name="platform" value={formData.platform} onChange={handleFieldChange} disabled={isDecided}>
                {PLATFORMS.map((p) => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </Select>
            </FieldBlock>
            <FieldBlock
              label="Rollout %"
              hint={isDecided ? 'stored value — 100% is served while decided' : '0–100'}
              htmlFor="f-exp-rollout"
            >
              <TextInput
                id="f-exp-rollout"
                name="rollout_percentage"
                type="number"
                min={0}
                max={100}
                value={formData.rollout_percentage}
                onChange={handleFieldChange}
                disabled={isDecided}
              />
            </FieldBlock>
          </FieldRow>
        </FormSection>

        <FormSection
          title="Variants"
          num="02"
          right={<Button type="button" variant="ghost" onClick={addVariant} disabled={isDecided}>+ Add variant</Button>}
        >
          {formData.variants.map((variant, idx) => (
            <FieldRow key={idx}>
              <FieldBlock label={idx === 0 ? 'Name' : undefined}>
                <TextInput
                  value={variant.name}
                  onChange={(e) => handleVariantChange(idx, 'name', e.target.value)}
                  placeholder="control"
                  disabled={isDecided}
                />
              </FieldBlock>
              <FieldBlock label={idx === 0 ? 'Weight' : undefined}>
                <TextInput
                  type="number"
                  min={0}
                  value={variant.weight}
                  onChange={(e) => handleVariantChange(idx, 'weight', e.target.value)}
                  placeholder="50"
                  disabled={isDecided}
                />
              </FieldBlock>
              <FieldBlock label={idx === 0 ? ' ' : undefined}>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => removeVariant(idx)}
                  disabled={isDecided || formData.variants.length <= 2}
                >
                  Remove
                </Button>
              </FieldBlock>
            </FieldRow>
          ))}
          <div className="text-text-muted" style={{ fontSize: 11.5, marginTop: 4 }}>
            {isDecided ? (
              <>
                Stored weights, kept for the record — while decided, <strong>{winningVariant}</strong> is
                served at 100% and every other variant at 0%.
              </>
            ) : (
              <>Weights are relative (need not sum to 100). At least one must be greater than 0.</>
            )}
          </div>
        </FormSection>

        <FormSection title="Analysis spec" num="03">
          <div className="font-condensed font-bold uppercase text-text-light" style={{ fontSize: 11, letterSpacing: '0.08em', marginBottom: 8 }}>
            Primary metric
          </div>
          <FieldRow>
            <FieldBlock label="Name" htmlFor="f-exp-pm-name">
              <TextInput
                id="f-exp-pm-name"
                name="name"
                value={primaryMetric.name}
                onChange={handlePrimaryMetricChange}
                placeholder="day7_retention"
              />
            </FieldBlock>
            <FieldBlock label="Definition" hint="warned at launch if missing" htmlFor="f-exp-pm-definition">
              <Textarea
                id="f-exp-pm-definition"
                name="definition"
                value={primaryMetric.definition}
                onChange={handlePrimaryMetricChange}
                rows={2}
              />
            </FieldBlock>
          </FieldRow>
          <FieldRow>
            <FieldBlock label="Numerator event" htmlFor="f-exp-pm-numerator">
              <TextInput
                id="f-exp-pm-numerator"
                name="numerator_event"
                value={primaryMetric.numerator_event}
                onChange={handlePrimaryMetricChange}
                placeholder="session_completed"
              />
            </FieldBlock>
            <FieldBlock label="Denominator event" htmlFor="f-exp-pm-denominator">
              <TextInput
                id="f-exp-pm-denominator"
                name="denominator_event"
                value={primaryMetric.denominator_event}
                onChange={handlePrimaryMetricChange}
                placeholder="experiment_exposure"
              />
            </FieldBlock>
          </FieldRow>

          <div className="flex items-center justify-between" style={{ marginTop: 20, marginBottom: 8 }}>
            <div className="font-condensed font-bold uppercase text-text-light" style={{ fontSize: 11, letterSpacing: '0.08em' }}>
              Secondary metrics
            </div>
            <Button type="button" variant="ghost" onClick={addSecondaryMetric}>+ Add secondary metric</Button>
          </div>
          {secondaryMetrics.map(({ uid, data: metric }, idx) => {
            // Rows after the first render no visible label, and every
            // Remove reads as just "Remove" — so each control names the
            // row it belongs to for screen-reader users.
            const rowLabel = (metric.name || '').trim() || `secondary metric ${idx + 1}`;
            return (
              <div key={uid} style={{ marginBottom: 12 }}>
                <FieldRow>
                  <FieldBlock label={idx === 0 ? 'Name' : undefined}>
                    <TextInput
                      value={metric.name}
                      onChange={(e) => handleSecondaryMetricChange(idx, 'name', e.target.value)}
                      placeholder="first_offer_upgrade_tap_rate"
                      aria-label={`Name for ${rowLabel}`}
                    />
                  </FieldBlock>
                  <FieldBlock label={idx === 0 ? 'Definition' : undefined} hint={idx === 0 ? 'warned at launch if missing' : undefined}>
                    <Textarea
                      value={metric.definition}
                      onChange={(e) => handleSecondaryMetricChange(idx, 'definition', e.target.value)}
                      rows={2}
                      aria-label={`Definition for ${rowLabel}`}
                    />
                  </FieldBlock>
                  <FieldBlock label={idx === 0 ? ' ' : undefined}>
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => removeSecondaryMetric(idx)}
                      aria-label={`Remove ${rowLabel}`}
                    >
                      Remove
                    </Button>
                  </FieldBlock>
                </FieldRow>
                <FieldRow>
                  <FieldBlock label={idx === 0 ? 'Numerator event' : undefined}>
                    <TextInput
                      value={metric.numerator_event}
                      onChange={(e) => handleSecondaryMetricChange(idx, 'numerator_event', e.target.value)}
                      aria-label={`Numerator event for ${rowLabel}`}
                    />
                  </FieldBlock>
                  <FieldBlock label={idx === 0 ? 'Denominator event' : undefined}>
                    <TextInput
                      value={metric.denominator_event}
                      onChange={(e) => handleSecondaryMetricChange(idx, 'denominator_event', e.target.value)}
                      aria-label={`Denominator event for ${rowLabel}`}
                    />
                  </FieldBlock>
                </FieldRow>
              </div>
            );
          })}
          {secondaryMetrics.length === 0 && (
            <div className="text-text-muted" style={{ fontSize: 11.5, marginBottom: 8 }}>
              No secondary metrics — these are diagnostics only and never gate a verdict.
            </div>
          )}

          <div className="flex items-center justify-between" style={{ marginTop: 20, marginBottom: 8 }}>
            <div className="font-condensed font-bold uppercase text-text-light" style={{ fontSize: 11, letterSpacing: '0.08em' }}>
              Guardrails
            </div>
            <Button type="button" variant="ghost" onClick={addGuardrail}>+ Add guardrail</Button>
          </div>
          {guardrails.map(({ uid, data: guardrail }, idx) => {
            const isKnownDirection = GUARDRAIL_DIRECTIONS.some((d) => d.value === guardrail.direction);
            const rowLabel = (guardrail.name || '').trim() || `guardrail ${idx + 1}`;
            return (
              <FieldRow key={uid}>
                <FieldBlock label={idx === 0 ? 'Name' : undefined}>
                  <TextInput
                    value={guardrail.name}
                    onChange={(e) => handleGuardrailChange(idx, 'name', e.target.value)}
                    placeholder="crash_free_rate"
                    aria-label={`Name for ${rowLabel}`}
                  />
                </FieldBlock>
                <FieldBlock label={idx === 0 ? 'Direction' : undefined}>
                  <Select
                    value={guardrail.direction}
                    onChange={(e) => handleGuardrailChange(idx, 'direction', e.target.value)}
                    aria-label={`Direction for ${rowLabel}`}
                  >
                    {GUARDRAIL_DIRECTIONS.map((d) => (
                      <option key={d.value} value={d.value}>{d.label}</option>
                    ))}
                    {/* A direction outside the known options (e.g. a value
                        introduced server-side after this form shipped)
                        renders as its raw value so the select isn't blank. */}
                    {!isKnownDirection && guardrail.direction && (
                      <option value={guardrail.direction}>{guardrail.direction}</option>
                    )}
                  </Select>
                </FieldBlock>
                <FieldBlock label={idx === 0 ? 'Definition' : undefined} hint={idx === 0 ? 'warned at launch if missing' : undefined}>
                  <Textarea
                    value={guardrail.definition}
                    onChange={(e) => handleGuardrailChange(idx, 'definition', e.target.value)}
                    rows={2}
                    placeholder="How this guardrail is computed, and any trap to avoid."
                    aria-label={`Definition for ${rowLabel}`}
                  />
                </FieldBlock>
                <FieldBlock label={idx === 0 ? ' ' : undefined}>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => removeGuardrail(idx)}
                    aria-label={`Remove ${rowLabel}`}
                  >
                    Remove
                  </Button>
                </FieldBlock>
              </FieldRow>
            );
          })}
          {guardrails.length === 0 && (
            <div className="text-text-muted" style={{ fontSize: 11.5, marginBottom: 8 }}>
              No guardrails yet.
            </div>
          )}

          <div className="font-condensed font-bold uppercase text-text-light" style={{ fontSize: 11, letterSpacing: '0.08em', marginTop: 20, marginBottom: 8 }}>
            Attribution
          </div>
          <FieldRow>
            <FieldBlock
              label="Assignment key"
              hint="the exposure-event property that assigns the arm — never an [Experiment] user property"
              htmlFor="f-exp-assignment-key"
            >
              <TextInput
                id="f-exp-assignment-key"
                value={attributionAssignmentKey}
                onChange={handleAttributionAssignmentKeyChange}
                placeholder="variant"
              />
            </FieldBlock>
          </FieldRow>

          <div className="font-condensed font-bold uppercase text-text-light" style={{ fontSize: 11, letterSpacing: '0.08em', marginTop: 20, marginBottom: 8 }}>
            Stop rule
          </div>
          <FieldRow>
            <FieldBlock label="Planned sample size" hint="per-variant exposures" htmlFor="f-exp-sample-size">
              <TextInput
                id="f-exp-sample-size"
                type="text"
                inputMode="numeric"
                value={plannedSampleSize}
                onChange={handleSampleSizeChange}
                placeholder="4000"
              />
            </FieldBlock>
            <FieldBlock label="Planned end date" htmlFor="f-exp-end-date">
              <DatePicker id="f-exp-end-date" value={plannedEndDate} onChange={handlePlannedEndDateChange} clearable />
            </FieldBlock>
          </FieldRow>
          <FieldRow>
            <FieldBlock
              label="Stop counter"
              hint="which counter the stop rule reads — they can legitimately differ"
              htmlFor="f-exp-stop-counter"
            >
              <Select id="f-exp-stop-counter" value={stopRuleCounterSource} onChange={handleStopRuleCounterSourceChange}>
                {STOP_RULE_COUNTER_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
                {/* A value outside the known options (e.g. introduced
                    server-side after this form shipped) renders as its raw
                    value so the select isn't blank. */}
                {stopRuleCounterSource && !STOP_RULE_COUNTER_SOURCES.includes(stopRuleCounterSource) && (
                  <option value={stopRuleCounterSource}>{stopRuleCounterSource}</option>
                )}
              </Select>
            </FieldBlock>
          </FieldRow>
          <FieldRow>
            <FieldBlock
              label="Baseline rate %"
              hint="the measured baseline the sample size was powered on"
              htmlFor="f-exp-baseline-rate"
            >
              <TextInput
                id="f-exp-baseline-rate"
                type="text"
                inputMode="decimal"
                value={baselineRate}
                onChange={handleBaselineRateChange}
                placeholder="23.6"
              />
            </FieldBlock>
            <FieldBlock
              label="Baseline denominator event"
              hint="the denominator the baseline was measured on — required when a baseline % is quoted"
              htmlFor="f-exp-baseline-denominator"
            >
              <TextInput
                id="f-exp-baseline-denominator"
                value={baselineDenominatorEvent}
                onChange={handleBaselineDenominatorEventChange}
                placeholder="experiment_exposure"
              />
            </FieldBlock>
          </FieldRow>
          <FieldRow>
            <FieldBlock
              label="Sample size rationale"
              hint="optional — baseline, MDE, power"
              htmlFor="f-exp-sample-size-rationale"
            >
              <Textarea
                id="f-exp-sample-size-rationale"
                value={sampleSizeRationale}
                onChange={handleSampleSizeRationaleChange}
                rows={2}
                placeholder="Baseline 23.6% (Amplitude, 60d); +3pp MDE at 80% power, two-sided alpha 0.05 needs 3,280/arm."
              />
            </FieldBlock>
          </FieldRow>

          <div className="font-condensed font-bold uppercase text-text-light" style={{ fontSize: 11, letterSpacing: '0.08em', marginTop: 20, marginBottom: 8 }}>
            Analysis hooks
          </div>
          <FieldRow>
            <FieldBlock label="Amplitude event names" htmlFor="f-exp-amp-events">
              <TagsInput
                inputId="f-exp-amp-events"
                value={amplitudeEventNames}
                onChange={handleAmplitudeEventNamesChange}
                placeholder="Event name and press enter…"
              />
            </FieldBlock>
            <FieldBlock label="Amplitude chart IDs" htmlFor="f-exp-amp-charts">
              <TagsInput
                inputId="f-exp-amp-charts"
                value={amplitudeChartIds}
                onChange={handleAmplitudeChartIdsChange}
                placeholder="Chart ID and press enter…"
              />
            </FieldBlock>
          </FieldRow>
        </FormSection>

        {isEdit && (
          <FormSection title="Assignment progress" num="04">
            <SummaryPanel summary={summary} slug={selectedSlug} winningVariant={winningVariant} />
          </FormSection>
        )}

        {isEdit && formData.slug && (
          <FormSection title="Test on device" num="05">
            <TestDeeplinkPanel slug={formData.slug} variants={formData.variants} />
          </FormSection>
        )}
      </form>
    </>
  );
}

/* ─── Test-on-device deeplinks ───────────────────────────────────
 * QA mechanism shared by every experiment: a custom-scheme deeplink the
 * iOS app handles (ExperimentOverride) to force a variant on a device, no
 * build change. Open the link (or scan the QR) on a non-production build,
 * then relaunch. App Store builds ignore overrides. Generic — derived
 * purely from the experiment's slug + variant names.
 */
const OVERRIDE_SCHEME = 'tappingsolution://debug';

function buildOverrideDeeplink(slug, variant) {
  return `${OVERRIDE_SCHEME}?experiment_override_slug=${encodeURIComponent(slug)}&variant=${encodeURIComponent(variant)}`;
}

function copyDeeplink(text) {
  if (navigator.clipboard?.writeText) {
    navigator.clipboard.writeText(text).then(
      () => Swal.fire({ icon: 'success', title: 'Copied', timer: 900, showConfirmButton: false }),
      () => Swal.fire({ icon: 'error', text: 'Could not copy to clipboard.' }),
    );
  }
}

function TestDeeplinkPanel({ slug, variants }) {
  const rows = [
    ...(variants || [])
      .map((v) => (v.name || '').trim())
      .filter(Boolean)
      .map((name) => ({ key: name, label: name, variant: name })),
    { key: '__reset__', label: 'Reset (clear)', variant: 'off' },
  ];

  return (
    <div>
      <div className="text-text-muted" style={{ fontSize: 12.5, marginBottom: 14, lineHeight: 1.5 }}>
        On a <strong>non-production</strong> build (debug / TestFlight), scan a code — or open the link — on the device to force
        this experiment&apos;s variant, then <strong>relaunch the app</strong>. App Store builds ignore overrides.
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
        {rows.map((row) => {
          const link = buildOverrideDeeplink(slug, row.variant);
          return (
            <div
              key={row.key}
              className="bg-surface border border-sand"
              style={{ borderRadius: 10, padding: 14, width: 196, boxSizing: 'border-box' }}
            >
              <div
                className="font-condensed font-bold uppercase text-text-light"
                style={{ fontSize: 11, letterSpacing: '0.08em', marginBottom: 10 }}
              >
                {row.label}
              </div>
              <div style={{ background: '#fff', padding: 8, borderRadius: 6, display: 'inline-block', lineHeight: 0 }}>
                <QRCodeSVG value={link} size={140} level="M" />
              </div>
              <code
                style={{ display: 'block', fontSize: 9.5, wordBreak: 'break-all', color: '#666', margin: '10px 0 8px' }}
              >
                {link}
              </code>
              <Button type="button" variant="ghost" onClick={() => copyDeeplink(link)}>
                Copy link
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ─── Summary panel ──────────────────────────────────────────── */
function SummaryPanel({ summary, slug, winningVariant }) {
  if (!summary || summary.slug !== slug) {
    return <div className="text-text-muted" style={{ fontSize: 13 }}>Loading assignment counts…</div>;
  }

  const planned = summary.planned_sample_size;
  const total = summary.total_assigned ?? 0;
  const variants = summary.variants || {};
  const pct = planned ? Math.min(100, Math.round((total / planned) * 100)) : null;

  return (
    <div>
      <div className="text-navy" style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>
        {total.toLocaleString()} assigned
        {planned ? <span className="text-text-muted" style={{ fontWeight: 400 }}> {' / '} {planned.toLocaleString()} planned ({pct}%)</span> : null}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
        {Object.entries(variants).map(([name, count]) => {
          const isWinner = !!winningVariant && name === winningVariant;
          return (
            <div
              key={name}
              className={cn('bg-surface border', isWinner ? 'border-navy' : 'border-sand')}
              style={{ borderRadius: 8, padding: '10px 14px', minWidth: 120 }}
            >
              <div
                className="font-condensed font-bold uppercase"
                style={{ fontSize: 11, letterSpacing: '0.08em' }}
              >
                <span className={isWinner ? 'text-navy' : 'text-text-light'}>{name}</span>
                {isWinner && (
                  <span className="text-navy" style={{ marginLeft: 6 }}>· winner</span>
                )}
              </div>
              <div className="text-navy" style={{ fontSize: 18, fontWeight: 700, marginTop: 2 }}>
                {Number(count).toLocaleString()}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
